import { z } from "zod";
import { MAX_GROUPED_ORDERS, MIN_GROUPED_ORDERS, type TitleStatus } from "@cx/domain";
import { listQuery } from "./common";
import { text } from "./fields";
import { ulidParam } from "./ids";
import { businessDate } from "./prices";
import type { SalesOrderStatusRef } from "./sales-orders";
import type { FiscalDocumentStatus } from "./fiscal-documents";

// ===== Faturamento agrupado (T2.7, RF-FIN-03) =====

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);

/**
 * Faturar pedidos atendidos do mesmo cliente: uma NF-e com os itens de todos (`issueInvoice`) e/ou um conjunto de
 * contas a receber sobre o total somado (`postReceivables`). `date` ausente = hoje (data base das parcelas);
 * `paymentTermId` nulo = uma parcela à vista na data; `taxOperationId` nulo = natureza do cliente ou a padrão de saída.
 */
export const groupedBillingInput = z
  .object({
    contactId: ulidParam,
    salesOrderIds: z
      .array(ulidParam, "Selecione os pedidos")
      .min(MIN_GROUPED_ORDERS, `Selecione ao menos ${MIN_GROUPED_ORDERS} pedidos`)
      .max(MAX_GROUPED_ORDERS, `No máximo ${MAX_GROUPED_ORDERS} pedidos por faturamento`),
    date: businessDate.optional(),
    paymentTermId: nullish(ulidParam),
    paymentMethodId: nullish(ulidParam),
    taxOperationId: nullish(ulidParam),
    issueInvoice: z.boolean().default(true),
    postReceivables: z.boolean().default(true),
    notes: text(500),
  })
  .superRefine((v, c) => {
    if (new Set(v.salesOrderIds).size !== v.salesOrderIds.length)
      c.addIssue({ code: "custom", path: ["salesOrderIds"], message: "Pedido repetido na seleção" });
    if (!v.issueInvoice && !v.postReceivables)
      c.addIssue({
        code: "custom",
        path: ["issueInvoice"],
        message: "Escolha gerar a NF-e, as contas a receber ou os dois",
      });
  });
export type GroupedBillingInput = z.output<typeof groupedBillingInput>;
export type GroupedBillingInputBody = z.input<typeof groupedBillingInput>;

export const groupedBillingReverseInput = z.object({ reason: text(500) });
export type GroupedBillingReverseInput = z.output<typeof groupedBillingReverseInput>;

export const groupedBillingListQuery = listQuery.extend({
  contactId: ulidParam.optional(),
  from: businessDate.optional(),
  to: businessDate.optional(),
  /** `active` = em vigor; `reversed` = desfeitos; ausente = todos. */
  state: z.enum(["active", "reversed"]).optional(),
});
export type GroupedBillingListQuery = z.output<typeof groupedBillingListQuery>;

/** Pedidos atendidos não faturados, por cliente: `contactId` (um cliente) ou `q` (nome/documento do cliente). */
export const groupedBillingCandidatesQuery = z.object({
  contactId: ulidParam.optional(),
  q: z.string().trim().max(100).optional(),
});
export type GroupedBillingCandidatesQuery = z.output<typeof groupedBillingCandidatesQuery>;

type Ref = { id: string; name: string } | null;
export type GroupedInvoiceRef = { id: string; status: FiscalDocumentStatus; number: number | null };

export type GroupedBillingCandidateOrder = {
  id: string;
  number: number;
  date: string;
  totalCents: number;
  status: SalesOrderStatusRef;
  sellerId: string | null;
  sellerName: string | null;
  paymentTermId: string | null;
  paymentTermName: string | null;
  /** Contas a receber próprias lançadas (passam para o agrupamento). */
  receivablesPosted: boolean;
  /** Conta própria com baixa: faturar com contas é recusado (estorne a baixa antes). */
  receivablesSettled: boolean;
};
export type GroupedBillingCandidateGroup = {
  contact: { id: string; name: string; document: string | null };
  orders: GroupedBillingCandidateOrder[];
  totalCents: number;
};
/** `truncated`: havia mais pedidos que o teto da consulta (filtre por cliente). */
export type GroupedBillingCandidates = { data: GroupedBillingCandidateGroup[]; truncated: boolean };

export type GroupedBillingFormOptions = {
  paymentTerms: { id: string; name: string }[];
  paymentMethods: { id: string; name: string }[];
  taxOperations: { id: string; name: string; isDefault: boolean }[];
};

export type GroupedBillingCreated = { id: string; number: number; fiscalDocumentId: string | null };

export type GroupedBillingListItem = {
  id: string;
  number: number;
  date: string;
  contactId: string;
  contactName: string;
  ordersCount: number;
  totalCents: number;
  issueInvoice: boolean;
  postReceivables: boolean;
  invoice: GroupedInvoiceRef | null;
  reversedAt: string | null;
};
export type GroupedBillingListResult = {
  data: GroupedBillingListItem[];
  nextCursor: string | null;
  /** Só na primeira página; `count` = -1 nas seguintes. */
  summary: { count: number; totalCents: number | null };
};

export type GroupedBillingInstallment = {
  titleId: string;
  number: number;
  dueDate: string;
  amountCents: number;
  paidCents: number;
  status: TitleStatus;
};

export type GroupedBillingView = {
  id: string;
  number: number;
  date: string;
  contact: { id: string; name: string; document: string | null };
  totalCents: number;
  ordersCount: number;
  issueInvoice: boolean;
  postReceivables: boolean;
  paymentTerm: Ref;
  paymentMethod: Ref;
  taxOperation: Ref;
  seller: Ref;
  notes: string | null;
  orders: { id: string; number: number; date: string; totalCents: number; hadReceivables: boolean }[];
  installments: GroupedBillingInstallment[];
  invoice: GroupedInvoiceRef | null;
  createdAt: string;
  reversedAt: string | null;
  reversalReason: string | null;
  /** Por que não dá para desfazer agora (NF-e emitida, conta com baixa); null = pode. */
  reverseBlocker: string | null;
};
